import { useMemo, useContext, useState, ReactElement } from 'react';
import { isString } from 'lodash-es';
import { Control } from 'react-hook-form';

import { Filter } from '..';
import { FilterType } from '@/models';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { FilterState } from '@/types/filter';
import { DropdownPosition } from '@/storybook/components/FormCollection/utils/utils';
import styles from './FilterSection.module.scss';
import FiltersCollapse from '@/storybook/components/FiltersCollapse/FiltersCollapse';

type SectifiedFilter = FilterType | FilterType[];

type FilterSectionProps = {
  label?: string;
  badge?: ReactElement;
  defaultStateOpen?: boolean;
  disabled?: boolean;
  formControl: Control<FilterState>;
  filterKeys?: SectifiedFilter[];
  onDropdownToggle?: (isOpen: boolean) => void;
  useFloatingLabel?: boolean;
  dropdownPosition?: DropdownPosition;
};

const FilterSection = ({
  label = '',
  badge,
  defaultStateOpen,
  disabled,
  filterKeys = [],
  formControl,
  onDropdownToggle,
  useFloatingLabel,
  dropdownPosition,
}: FilterSectionProps) => {
  const { activeFilters } = useContext(FiltersContext);
  const [open, setOpen] = useState(defaultStateOpen || false);

  const activeChildFiltersCount = activeFilters
    .filter(activeFilter => filterKeys.flat().includes(activeFilter.key))
    .map(activeFilter => activeFilter.key).length;

  const children = useMemo(() => {
    return filterKeys.map(section => {
      if (isString(section)) {
        return (
          <div key={section} className={styles.sectionWrapper}>
            <Filter
              control={formControl}
              filterKey={section}
              name={section}
              onDropdownToggle={onDropdownToggle}
              useFloatingLabel={useFloatingLabel}
              dropdownPosition={dropdownPosition}
            />
          </div>
        );
      } else {
        const concatenatedSubKey = section.join('_');
        return (
          <div key={`section-${concatenatedSubKey}`} className={styles.sectionWrapper}>
            {section.map(key => (
              <Filter
                key={key}
                control={formControl}
                filterKey={key}
                name={key}
                onDropdownToggle={onDropdownToggle}
                useFloatingLabel={useFloatingLabel}
                dropdownPosition={dropdownPosition}
              />
            ))}
          </div>
        );
      }
    });
  }, [filterKeys, formControl, onDropdownToggle, useFloatingLabel, dropdownPosition]);

  const handleToggle = () => {
    setOpen(prevState => !prevState);
  };

  return (
    <FiltersCollapse
      label={label}
      badge={badge}
      defaultStateOpen={defaultStateOpen}
      disabled={disabled}
      open={open}
      onToggle={handleToggle}
      activeFiltersCount={activeChildFiltersCount}
      filterKeys={filterKeys}
    >
      <div className={styles.panelWrapper}>{children}</div>
    </FiltersCollapse>
  );
};

export { FilterSection };
